'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useState, } from 'react';
import { createPortal } from 'react-dom';
import ModalController from './modal-confirm-controller';
const ModalConfirmComponent = lazy(() => import('../../../components/common/hd-modal/confirm').then((module) => ({
    default: module.ModalConfirmComponent,
})));
const Popup = lazy(() => import('../../../components/common/hd-modal/popup'));
const generateFallbackModalId = () => {
    return typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);
};
const ModalContext = createContext(undefined);
ModalContext.displayName = 'ModalContext';
export const ModalProvider = ({ children }) => {
    const controller = ModalController.getInstance();
    const [modals, setModals] = useState({});
    const isModalOpen = useCallback((id) => id in modals, [modals]);
    const deleteModal = useCallback((id) => {
        setModals((prev) => {
            const _a = prev, _b = id, _ = _a[_b], rest = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
            return rest;
        });
    }, []);
    const upsertModal = useCallback((options) => {
        var _a;
        const id = (_a = options.id) !== null && _a !== void 0 ? _a : generateFallbackModalId();
        setModals((prev) => (Object.assign(Object.assign({}, prev), { [id]: { type: 'default', props: options } })));
    }, []);
    const confirm = useCallback((options) => {
        const id = generateFallbackModalId();
        return new Promise((resolve) => {
            setModals((prev) => (Object.assign(Object.assign({}, prev), { [id]: {
                    type: 'confirm',
                    props: options,
                    resolver: resolve,
                } })));
        });
    }, []);
    const handleClose = useCallback((id, state) => {
        setModals((prev) => {
            var _a, _b, _c;
            const _d = prev, _e = id, modal = _d[_e], rest = __rest(_d, [typeof _e === "symbol" ? _e : _e + ""]);
            if (!modal)
                return prev;
            if (modal.type === 'confirm') {
                state === 'confirm'
                    ? (_a = modal.resolver) === null || _a === void 0 ? void 0 : _a.call(modal, true)
                    : (_b = modal.resolver) === null || _b === void 0 ? void 0 : _b.call(modal, false);
            }
            else {
                const props = modal.props;
                (_c = props.handleCancel) === null || _c === void 0 ? void 0 : _c.call(props);
            }
            return rest;
        });
    }, []);
    useEffect(() => {
        controller.setConfirmFn(confirm);
    }, [confirm, controller]);
    return (_jsxs(ModalContext.Provider, { value: { confirm, upsertModal, handleClose, isModalOpen, deleteModal }, children: [children, typeof document !== 'undefined'
                ? createPortal(_jsx(Suspense, { fallback: null, children: Object.entries(modals).map(([id, { type, props }]) => {
                        if (type === 'confirm') {
                            const confirmProps = props;
                            return (_jsx(ModalConfirmComponent, Object.assign({}, confirmProps, { onOk: async () => {
                                    await confirmProps.onOk();
                                    handleClose(id, 'confirm');
                                }, onCancel: async () => {
                                    var _a;
                                    await ((_a = confirmProps.onCancel) === null || _a === void 0 ? void 0 : _a.call(confirmProps));
                                    handleClose(id, 'cancel');
                                }, onClose: () => handleClose(id) }), id));
                        }
                        const modalProps = props;
                        return (_jsx(Popup, Object.assign({}, modalProps, { handleCancel: () => handleClose(id) }), id));
                    }) }), document.body)
                : null] }));
};
export const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModal must be used within a ModalProvider');
    }
    return context;
};
